import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { fetchUserUsage } from "../../utils/apis";
import { rangeProblem } from "../../helpers/usageRange";
import UsageRangeFilter from "./UsageRangeFilter";
import { customerName } from "./customerName";

// One customer's requests per service. Starts on the period the Usage tab is showing.
const UserUsageDialog = ({ user, initialRange, onClose }) => {
  const [range, setRange] = useState(initialRange);
  const [state, setState] = useState({ status: "loading", rows: [] });
  const [attempt, setAttempt] = useState(0);
  const { startDate, endDate } = range;
  const problem = rangeProblem(range);

  useEffect(() => {
    if (problem) return undefined;
    let ignore = false;
    setState({ status: "loading", rows: [] });
    fetchUserUsage(user._id, { startDate, endDate }).then((result) => {
      if (ignore) return;
      if (!result) setState({ status: "error", rows: [] });
      else if (result.ready === false) setState({ status: "building", rows: [] });
      else setState({ status: "ready", rows: result.rows });
    });
    return () => {
      ignore = true;
    };
  }, [user._id, startDate, endDate, problem, attempt]);

  const { status, rows } = state;
  const total = rows.reduce((sum, row) => sum + row.count, 0);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="usage-dialog-title">
      <DialogTitle id="usage-dialog-title">
        {customerName(user)}
        {user.deleted && <span className="usage-badge ms-2">Deleted account</span>}
      </DialogTitle>
      <DialogContent>
        <p className="text-muted small mb-3">
          {[user.companyName, user.email].filter(Boolean).join(" · ") || "Requests per service"}
          {" · days are counted in UTC"}
        </p>
        <UsageRangeFilter value={range} onChange={setRange} />
        <div className="mt-3" aria-live="polite">
          {problem ? null : status === "error" ? (
            <p className="usage-state usage-state-error">
              Could not load this customer's usage.{" "}
              <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => setAttempt((n) => n + 1)}>
                Try again
              </button>
            </p>
          ) : status === "building" ? (
            <p className="usage-state">Analytics is still being set up, so there are no numbers yet.</p>
          ) : status === "loading" ? (
            <p className="usage-state">Loading…</p>
          ) : (
            <table className="table usage-breakdown">
              <thead>
                <tr>
                  <th scope="col">Service</th>
                  <th scope="col" className="text-end">
                    Requests
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.serviceType} className={row.count === 0 ? "usage-zero" : ""}>
                    <td>{row.name}</td>
                    <td className="text-end">{row.count.toLocaleString()}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={2} className="text-muted">
                      No services are set up.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Total</th>
                  <td className="text-end fw-bold">{total.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserUsageDialog;
